import { lazy, ComponentType } from 'react';

// Recovers from stale chunk references after a redeploy. When a dynamic import
// fails because the hashed JS file no longer exists, reload the page once so
// the browser fetches the new index.html and resolves the new hashes. A
// sessionStorage flag prevents infinite reload loops when the chunk is truly
// broken.

const RELOAD_KEY = 'chunk-reload-attempted';

function isChunkLoadError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const msg = (err as Error).message || '';
  const name = (err as Error).name || '';
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

export default function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      window.sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      if (isChunkLoadError(err) && !window.sessionStorage.getItem(RELOAD_KEY)) {
        window.sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
